import { debounce } from "lodash";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addProperty,
  getFolderPrimaryKey,
  getFolderPropertyTypes,
  getFolderSecIndexes,
  getPropertyValues,
  renameProperty,
  setPropertyValue as setPropertyValueApi,
  writeFolderPrimaryKey,
  writeFolderSecIndexes,
  writePropertyTypes,
} from "../../api/page/property";
import { useAppId } from "../../hook";
import { ActionType, AppDispatch, RootState } from "../../reducer";
import {
  DeletePropertyTypeAction,
  SetFolderPrimaryKeyAction,
  SetFolderPropertiesAction,
  SetFolderSecIndexesAction,
  SetPagePropertyValuesAction,
} from "../../reducer/note/property";
import { getPageIdForProperties } from "../../reducer/note/property/util";
import { isNotePageData } from "../../types/note";
import {
  FolderPropertyTypesMap,
  PagePropertyValuesMap,
  PropertyType,
  PropertyTypesMap,
  PropertyValue,
  PropertyValuesMap,
} from "../../types/page/property";
import { useSelectedNotebookPage, useSelectedNotebookPageIdMap } from "../page";
import { useFetchDatabaseViews } from "./view";
import * as R from "ramda";

export const useFolderPropertyTypes = (folderId: string | undefined) => {
  const folderProps = useSelector((state: RootState) => {
    const folderPropsMap = state.ui.folderPropertyTypes;
    const folderProps = folderId ? folderPropsMap[folderId] : undefined;
    return folderProps;
  });
  const setFolderPropertyTypes = useSetFolderPropertyTypes();
  const setFolderProps = (props: PropertyTypesMap) => {
    if (!folderId) return;
    const newFolderProps: FolderPropertyTypesMap = { [folderId]: props };
    setFolderPropertyTypes(newFolderProps);
  };
  return { folderProps, setFolderProps };
};

export const useFolderPrimaryKey = (folderId: string | undefined) => {
  const folderPKey = useSelector((state: RootState) =>
    folderId ? state.ui.folderPrimaryKeys[folderId] : undefined,
  );
  const dispatch = useDispatch<AppDispatch>();
  const setFolderPKey = (pKey: string[]) => {
    if (!folderId) return;
    const action: SetFolderPrimaryKeyAction = {
      type: ActionType.SET_FOLDER_PRIMARY_KEY,
      folderId,
      pKey,
    };
    dispatch(action as any);
  };
  return { folderPKey, setFolderPKey };
};

export const useSetFolderPropertyTypes = () => {
  const dispatch = useDispatch();

  const setFolderPropertyTypes = (folderProps: FolderPropertyTypesMap) => {
    const action: SetFolderPropertiesAction = {
      type: ActionType.SET_FOLDER_PROPERTIES,
      folderProps,
    };
    dispatch(action as any);
  };
  return setFolderPropertyTypes;
};

export const useFetchFolderPropertyTypes = () => {
  const setPropertyTypes = useSetFolderPropertyTypes();

  const fetchProperties = (folderId: string) => {
    getFolderPropertyTypes({ folderId }).then((res) => {
      const propMap = { [res["folder-id"]]: res.properties };
      setPropertyTypes(propMap);
    });
  };

  return fetchProperties;
};

export const useSetFolderPropertyType = (
  folderId: string | undefined,
  propName: string | undefined,
) => {
  const { folderProps, setFolderProps } = useFolderPropertyTypes(folderId);

  const setFolderProp = (prop: PropertyType) => {
    if (!folderProps) return;
    if (!propName) return;
    const newPropName = prop.name;
    const updateFn = R.pipe(R.dissoc(propName), R.assoc(newPropName, prop));
    const newProps: PropertyTypesMap = updateFn(folderProps);
    setFolderProps(newProps);
    return newProps;
  };

  return { setFolderProp };
};

export const useSetFolderPropertyTypeIsPKey = (
  folderId: string | undefined,
  propName: string | undefined,
) => {
  const { folderPKey, setFolderPKey } = useFolderPrimaryKey(folderId);

  const setFolderPropIsPKey = (isPKey: boolean) => {
    if (!folderPKey) return;
    if (!propName) return;
    const append = () => R.append(propName, folderPKey);
    const includes = () => folderPKey.includes(propName);
    const add = () => (includes() ? folderPKey : append());
    const remove = () => R.without([propName], folderPKey);
    const newPKey = isPKey ? add() : remove();
    setFolderPKey(newPKey);
    return newPKey;
  };

  return { setFolderPropIsPKey };
};

export const useDeleteProperty = (folderId) => {
  const [appId] = useAppId();
  const { folderProps, setFolderProps } = useFolderPropertyTypes(folderId);
  const { folderPKey } = useFolderPrimaryKey(folderId);
  const writeBackend = useWriteFolderPropertyTypesAndPKey(folderId);

  return (propName: string) => {
    if (!folderId) return;
    if (!propName) return;
    if (!folderPKey) return;
    if (!folderProps) return;
    const newFolderProps = R.dissoc(propName, folderProps);
    setFolderProps(newFolderProps);
    writeBackend({ folderProps: newFolderProps, pKey: folderPKey });
  };
};
export const useFetchDatabasePropertyValues = () => {
  const [appId] = useAppId();
  const dispatch = useDispatch();

  const fetchProperties = (config: {
    folderId: string;
    pageIds?: string[];
  }) => {
    const { folderId, pageIds } = config;
    if (!appId) return;
    const pageIdsEntry = pageIds ? { "page-ids": pageIds } : {};
    const requestConfig = {
      "app-id": appId,
      "folder-id": folderId,
      ...pageIdsEntry,
    };
    if (!pageIds || pageIds.length === 0) {
      return;
    }

    getPropertyValues(requestConfig).then((res) => {
      const pagePropVals = res["prop-values"];
      const action: SetPagePropertyValuesAction = {
        type: ActionType.SET_PAGE_PROPERTY_VALUES,
        propVals: pagePropVals,
      };
      dispatch(action as any);
    });
  };

  return fetchProperties;
};

export const useFetchDatabasePropertyValuesListener = () => {
  const page = useSelectedNotebookPage();
  const folderId = page?.["page-id"];
  const fetchDatabasePropertyValues = useFetchDatabasePropertyValues();

  useEffect(() => {
    if (!folderId) return;
    fetchDatabasePropertyValues({ folderId });
  }, [folderId]);
};

export const useFetchDatabaseViewsListener = () => {
  const page = useSelectedNotebookPage();
  const folderId = page?.["page-id"];
  const fetchDatabaseViews = useFetchDatabaseViews();

  useEffect(() => {
    if (!folderId) return;
    fetchDatabaseViews({ pageId: folderId });
  }, [folderId]);
};

export const useFetchFolderPropertyTypesListener = (
  folderId: string | undefined,
) => {
  const fetchFolderProperties = useFetchFolderPropertyTypes();
  useEffect(() => {
    if (!folderId) return;
    fetchFolderProperties(folderId);
  }, [folderId]);
};

export const useAddPropertyType: () => (config: {
  data: PropertyType;
}) => void = () => {
  const fetchFolderProps = useFetchFolderPropertyTypes();

  return (prop) => {
    const { data } = prop;

    addProperty({ data }).then((res) => {
      fetchFolderProps(data["folder-id"]);
    });
  };
};

export const useGetProperty: () => (
  folderId: string,
  propName: string,
) => PropertyType = () => {
  const folderPropertyTypes = useSelector((state: RootState) => state.ui.folderPropertyTypes);
  return (folderId, propName) => {
    const folderMap = folderPropertyTypes[folderId] ?? {};
    const prop = folderMap[propName];
    return prop;
  };
};

export const useFetchPagePropertyValues = () => {
  const [appId] = useAppId();
  const page = useSelectedNotebookPage();
  const pageId = page?.["page-id"]!;
  const pageData = page?.["page-data"]!;
  const dispatch = useDispatch<AppDispatch>();

  const fetchPagePropertyValues = () => {
    if (!appId || !page) return;

    if (pageData && isNotePageData(pageData)) {
      getPropertyValues({ "app-id": appId, "page-id": pageId }).then(
        (props) => {
          const propTyes = props.props;
          const pagePropVals = props["prop-values"];
          const action: SetPagePropertyValuesAction = {
            type: ActionType.SET_PAGE_PROPERTY_VALUES,
            propVals: pagePropVals,
          };
          dispatch(action as any);
          // const actionTypes: SetPagePropertiesAction = {
          //   type: ActionType.ADD_PRO,
          //   propVals: pagePropVals,
          // };
          // dispatch(action as any);
        },
      );
    }
  };

  return fetchPagePropertyValues;
};

export const useFetchPagePropertyValuesListener = () => {
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const fetchPagePropertyValues = useFetchPagePropertyValues();

  useEffect(() => {
    fetchPagePropertyValues();
  }, [selectedNotebookPageId]);
};

export const useWriteFolderPropertyTypesAndPKey = (
  folderId: string | undefined,
) => {
  return async (config: {
    folderProps: PropertyTypesMap;
    pKey: string[];
    secIndexes?: string[];
  }) => {
    if (!folderId) return;
    const { folderProps, pKey, secIndexes } = config;
    await writePropertyTypes({ properties: folderProps, "folder-id": folderId });
    await writeFolderPrimaryKey({ "primary-key": pKey, "folder-id": folderId });
    if (secIndexes) {
      await writeFolderSecIndexes({
        "folder-id": folderId,
        "sec-indexes": secIndexes,
      });
    }
  };
};

export const useFetchFolderPrimaryKey = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (folderId: string) => {
    getFolderPrimaryKey({ "folder-id": folderId }).then((pKey) => {
      const action: SetFolderPrimaryKeyAction = {
        type: ActionType.SET_FOLDER_PRIMARY_KEY,
        folderId,
        pKey,
      };
      dispatch(action as any);
    });
  };
};

export const useFetchDatabasePrimaryKeyListener = () => {
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const fetchPKey = useFetchFolderPrimaryKey();
  useEffect(() => {
    if (!selectedNotebookPageId) return;
    fetchPKey(selectedNotebookPageId);
  }, [selectedNotebookPageId]);
};

export const useFolderSecIndexes = (folderId: string | undefined) => {
  const folderSecIndexes = useSelector((state: RootState) =>
    folderId ? state.ui.folderSecIndexes[folderId] : undefined,
  );
  const dispatch = useDispatch<AppDispatch>();
  const setFolderSecIndexes = (secIndexes: string[]) => {
    if (!folderId) return;
    const action: SetFolderSecIndexesAction = {
      type: ActionType.SET_FOLDER_SEC_INDEXES,
      folderId,
      secIndexes,
    };
    dispatch(action as any);
  };
  return { folderSecIndexes, setFolderSecIndexes };
};

export const useFetchFolderSecIndexes = () => {
  const dispatch = useDispatch<AppDispatch>();
  return (folderId: string) => {
    getFolderSecIndexes({ "folder-id": folderId }).then((secIndexes) => {
      const action: SetFolderSecIndexesAction = {
        type: ActionType.SET_FOLDER_SEC_INDEXES,
        folderId,
        secIndexes,
      };
      dispatch(action as any);
    });
  };
};

export const useRenameProperty = (folderId: string | undefined) => {
  const fetchFolderProperties = useFetchFolderPropertyTypes();
  const fetchFolderPrimaryKey = useFetchFolderPrimaryKey();
  const fetchFolderSecIndexes = useFetchFolderSecIndexes();

  return async (oldName: string, newName: string) => {
    if (!folderId || !oldName || !newName || oldName === newName) return;
    await renameProperty({ "folder-id": folderId, "old-name": oldName, "new-name": newName });
    fetchFolderProperties(folderId);
    fetchFolderPrimaryKey(folderId);
    fetchFolderSecIndexes(folderId);
  };
};

export const useFetchFolderSecIndexesListener = () => {
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const fetchSecIndexes = useFetchFolderSecIndexes();
  useEffect(() => {
    if (!selectedNotebookPageId) return;
    fetchSecIndexes(selectedNotebookPageId);
  }, [selectedNotebookPageId]);
};

export const useSetFolderPropertyTypeIsSecIndex = (
  folderId: string | undefined,
  propName: string | undefined,
) => {
  const { folderSecIndexes, setFolderSecIndexes } =
    useFolderSecIndexes(folderId);

  const setFolderPropIsSecIndex = (isSecIndex: boolean) => {
    const current = folderSecIndexes ?? [];
    if (!propName) return current;
    const append = () => R.append(propName, current);
    const includes = () => current.includes(propName);
    const add = () => (includes() ? current : append());
    const remove = () => R.without([propName], current);
    const newSecIndexes = isSecIndex ? add() : remove();
    setFolderSecIndexes(newSecIndexes);
    return newSecIndexes;
  };

  return { setFolderPropIsSecIndex };
};

export const useGetPropertyValue: () => (
  pageId: string,
  propName: string,
) => PropertyValue | undefined = () => {
  const getPropValues = useGetPropertyValues();

  return (pageId, propName) => {
    const propVals = getPropValues(pageId);
    return propVals && propName in propVals ? propVals[propName] : undefined;
  };
};

export const usePropertyValues: (
  pageId: string,
) => PropertyValuesMap | undefined = (pageId) => {
  const propVals = useSelector((state: RootState) => {
    const pagePropVals = state.ui.pagePropertyValues;
    const propVals = pageId in pagePropVals ? pagePropVals[pageId] : undefined;
    return propVals;
  });

  return propVals;
};

export const useSetPropertyValue: () => (
  pageId: string,
  propName: string,
  propVal: PropertyValue,
) => void = () => {
  const dispatch = useDispatch();
  const writeBackend = (pageId, propName, propVal) => {
    return setPropertyValueApi({
      "page-id": pageId,
      "prop-name": propName,
      "prop-val": propVal,
    });
  };
  const getVals = useGetPropertyValues();
  const writeBackendCallback = useMemo(
    () =>
      debounce(
        (pageId, propName, propVal) => writeBackend(pageId, propName, propVal),
        250,
      ),
    [],
  );

  const setPropVal = (
    pageId: string,
    propName: string,
    propVal: PropertyValue,
  ) => {
    const curentValues = getVals(pageId) ?? {};
    const newVals: PropertyValuesMap = { ...curentValues, [propName]: propVal };
    const pagePropVals: PagePropertyValuesMap = { [pageId]: newVals };
    const action: SetPagePropertyValuesAction = {
      type: ActionType.SET_PAGE_PROPERTY_VALUES,
      propVals: pagePropVals,
    };
    dispatch(action as any);
    writeBackendCallback(pageId, propName, propVal);
  };
  return setPropVal;
};

export const usePropertyValue = (pageId: string, propName: string) => {
  const getPropVal = useGetPropertyValue();
  const setPropVal = useSetPropertyValue();
  const setter = (val: PropertyValue) => setPropVal(pageId, propName, val);
  const getter = getPropVal(pageId, propName);
  return [getter, setter];
};

export const useGetPropertyValues: () => (
  pageId: string,
) => PropertyValuesMap | undefined = () => {
  const pagePropVals = useSelector(
    (state: RootState) => state.ui.pagePropertyValues,
  );
  const getPropVals = (pageId) => {
    return pageId in pagePropVals ? pagePropVals[pageId] : undefined;
  };
  return getPropVals;
};

export const useIsPropertiesDefinedForPage: () => (
  pageId: string,
) => boolean = () => {
  const pagePropVals = useSelector((state: RootState) => {
    const pagePropVals = state.ui.pagePropertyValues;
    return pagePropVals;
  });

  return (pageId) => {
    return (
      pageId in pagePropVals && Object.keys(pagePropVals[pageId]).length !== 0
    );
  };
};
